/**
 * @module engine/render
 * @summary Immediate-mode frame renderer + scrollbar/minimap painters.
 * @description
 *   `drawAll` repaints the whole canvas each dirty frame. The ONE change vs the
 *   original is high-DPI support: the base transform is scaled by
 *   `devicePixelRatio` (S.dpr) so the canvas is crisp on Retina/iPad. All world
 *   maths stays in CSS pixels — DPR is folded into the transform only.
 *   // INVARIANT: this module reads engine state and the canvas context; it
 *   never mutates document data.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3230-3362 (drawAll,
 *   getWorldBounds, updateScrollbars) and 4032-4075 (drawMinimap).
 */
import { S, GRID } from './state';
import { drawFn, drawLabel } from './shapes/drawFns';
import { drawConn } from './routing';
import { computeBalance, streamMaterial, streamRateText } from './balance';
import { getPorts, getHandles } from './geometry';
import { registry } from './shapes/registry';
import { toW } from './coords';

/** Minimap collapsed flag (UI state owned by the engine for the render loop). */
let mmCollapsed = false;
export function setMinimapCollapsed(v: boolean): void {
  mmCollapsed = v;
  S.dirty = true;
}
export function isMinimapCollapsed(): boolean {
  return mmCollapsed;
}

/** Bounding box of all shapes (+margin) used for scrollbars/minimap. */
export function getWorldBounds(): { x: number; y: number; w: number; h: number } {
  const margin = 400;
  if (!S.shapes.length) return { x: -500, y: -500, w: 2000, h: 1500 };
  const xs = S.shapes.map((s) => s.x);
  const ys = S.shapes.map((s) => s.y);
  const xe = S.shapes.map((s) => s.x + s.w);
  const ye = S.shapes.map((s) => s.y + s.h);
  return {
    x: Math.min(...xs) - margin,
    y: Math.min(...ys) - margin,
    w: Math.max(...xe) - Math.min(...xs) + margin * 2,
    h: Math.max(...ye) - Math.min(...ys) + margin * 2,
  };
}

/** Update the custom scrollbar thumbs (engine-owned DOM, queried by id). */
export function updateScrollbars(): void {
  const wb = getWorldBounds();
  const vpW = S.CW / S.zoom;
  const vpH = S.CH / S.zoom;
  const vpX = -S.panX / S.zoom;
  const vpY = -S.panY / S.zoom;
  const hEl = document.getElementById('h-scrollbar');
  const hTk = hEl ? hEl.clientWidth : 400;
  const hTW = Math.max(24, hTk * Math.min(1, vpW / wb.w));
  const hP = Math.max(0, Math.min(1, (vpX - wb.x) / Math.max(1, wb.w - vpW)));
  const hThumb = document.getElementById('h-scrollbar-thumb');
  if (hThumb) {
    hThumb.style.width = hTW + 'px';
    hThumb.style.left = hP * (hTk - hTW) + 'px';
  }
  const vEl = document.getElementById('v-scrollbar');
  const vTk = vEl ? vEl.clientHeight : 400;
  const vTH = Math.max(24, vTk * Math.min(1, vpH / wb.h));
  const vP = Math.max(0, Math.min(1, (vpY - wb.y) / Math.max(1, wb.h - vpH)));
  const vThumb = document.getElementById('v-scrollbar-thumb');
  if (vThumb) {
    vThumb.style.height = vTH + 'px';
    vThumb.style.top = vP * (vTk - vTH) + 'px';
  }
}

/** Paint the overview minimap (engine-owned #minimap-canvas). */
export function drawMinimap(): void {
  const mmCanvas = document.getElementById('minimap-canvas') as HTMLCanvasElement | null;
  const mmCtx = mmCanvas ? mmCanvas.getContext('2d') : null;
  if (!mmCtx || !mmCanvas || mmCollapsed) return;
  const W = mmCanvas.width;
  const H = mmCanvas.height;
  mmCtx.clearRect(0, 0, W, H);
  mmCtx.fillStyle = '#f5f5f5';
  mmCtx.fillRect(0, 0, W, H);
  if (!S.shapes.length) {
    mmCtx.fillStyle = '#aaa';
    mmCtx.font = '10px Segoe UI';
    mmCtx.textAlign = 'center';
    mmCtx.fillText('Empty canvas', W / 2, H / 2);
    return;
  }
  const wb = getWorldBounds();
  const sc = Math.min(W / wb.w, H / wb.h) * 0.92;
  const ox = (W - wb.w * sc) / 2 - wb.x * sc;
  const oy = (H - wb.h * sc) / 2 - wb.y * sc;
  S.shapes.forEach((s) => {
    if (s.type === 'zonebox') {
      mmCtx.fillStyle = 'rgba(30,30,30,0.08)';
      mmCtx.fillRect(s.x * sc + ox, s.y * sc + oy, s.w * sc, s.h * sc);
      mmCtx.strokeStyle = 'rgba(43,87,154,0.4)';
      mmCtx.lineWidth = 0.6;
      mmCtx.strokeRect(s.x * sc + ox, s.y * sc + oy, s.w * sc, s.h * sc);
    } else {
      mmCtx.fillStyle = '#dae3f3';
      mmCtx.fillRect(s.x * sc + ox, s.y * sc + oy, Math.max(2, s.w * sc), Math.max(2, s.h * sc));
      mmCtx.strokeStyle = '#1a1a1a';
      mmCtx.lineWidth = 0.5;
      mmCtx.strokeRect(s.x * sc + ox, s.y * sc + oy, Math.max(2, s.w * sc), Math.max(2, s.h * sc));
    }
  });
  mmCtx.strokeStyle = '#666';
  mmCtx.lineWidth = 0.4;
  S.connections.forEach((cn) => {
    const f = S.shapes.find((s) => s.id === cn.fromId);
    const t = S.shapes.find((s) => s.id === cn.toId);
    if (!f || !t) return;
    mmCtx.beginPath();
    mmCtx.moveTo((f.x + f.w / 2) * sc + ox, (f.y + f.h / 2) * sc + oy);
    mmCtx.lineTo((t.x + t.w / 2) * sc + ox, (t.y + t.h / 2) * sc + oy);
    mmCtx.stroke();
  });
  // Viewport rectangle.
  const vx = -S.panX / S.zoom;
  const vy = -S.panY / S.zoom;
  const vw = S.CW / S.zoom;
  const vh = S.CH / S.zoom;
  mmCtx.strokeStyle = '#0078d4';
  mmCtx.lineWidth = 1.5;
  mmCtx.strokeRect(vx * sc + ox, vy * sc + oy, vw * sc, vh * sc);
  mmCtx.fillStyle = 'rgba(0,120,212,0.08)';
  mmCtx.fillRect(vx * sc + ox, vy * sc + oy, vw * sc, vh * sc);
}

/** The main render pass. Called by the engine's RAF loop when dirty. */
export function drawAll(): void {
  const ctx = S.ctx;
  if (!ctx) return;
  const { CW, CH, zoom, panX, panY, dpr } = S;
  const selSet = new Set(S.selected);

  // Base transform: 1 unit = 1 CSS px, rendered at device resolution (DPR).
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, CW, CH);
  // Grey surround.
  ctx.fillStyle = '#d4d4d4';
  ctx.fillRect(0, 0, CW, CH);
  // White page with shadow.
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.22)';
  ctx.shadowBlur = 10;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(panX, panY, 2000 * zoom, 1500 * zoom);
  ctx.restore();
  // Grid dots, clipped to the white page.
  if (S.gridOn) {
    const sp = GRID * zoom;
    if (sp >= 6) {
      const pgX = panX;
      const pgY = panY;
      const pgW = 2000 * zoom;
      const pgH = 1500 * zoom;
      const x0 = Math.max(0, pgX);
      const y0 = Math.max(0, pgY);
      const x1 = Math.min(CW, pgX + pgW);
      const y1 = Math.min(CH, pgY + pgH);
      if (x1 > x0 && y1 > y0) {
        const ox = ((panX % sp) + sp) % sp;
        const oy = ((panY % sp) + sp) % sp;
        const dr = Math.min(1.2, Math.max(0.6, zoom * 0.8));
        ctx.save();
        ctx.beginPath();
        ctx.rect(x0, y0, x1 - x0, y1 - y0);
        ctx.clip();
        ctx.fillStyle = 'rgba(0,0,0,0.1)';
        ctx.beginPath();
        for (let gx = ox; gx < x1; gx += sp) {
          if (gx < x0) continue;
          for (let gy = oy; gy < y1; gy += sp) {
            if (gy < y0) continue;
            ctx.moveTo(gx, gy);
            ctx.arc(gx, gy, dr, 0, Math.PI * 2);
          }
        }
        ctx.fill();
        ctx.restore();
      }
    }
  }

  // ── World content (DPR × zoom × pan) ──
  ctx.save();
  ctx.setTransform(dpr * zoom, 0, 0, dpr * zoom, dpr * panX, dpr * panY);

  // Zone boxes first (below everything).
  S.shapes
    .filter((s) => s.type === 'zonebox')
    .forEach((s) => {
      ctx.save();
      drawFn(ctx, s);
      ctx.restore();
    });
  // Connections. The whole drawing is balanced once here, not once per line:
  // a throughput is a property of the sheet, and every stream on it is worked
  // out in the same sweep. With no rates typed anywhere the balance is empty
  // and every line draws exactly as it always did.
  const bal = computeBalance();
  S.connections.forEach((cn) => {
    drawConn(ctx, cn, selSet.has(cn.id), streamRateText(bal, cn.id), streamMaterial(bal, cn.id));
  });
  // Shapes, z-sorted + off-screen culled.
  const vL = -panX / zoom;
  const vT = -panY / zoom;
  const vR = vL + CW / zoom;
  const vB = vT + CH / zoom;
  const inView = (s: { x: number; y: number; w: number; h: number }) =>
    !(s.x + s.w < vL || s.x > vR || s.y + s.h < vT || s.y > vB);
  S.shapes
    .filter((s) => s.type !== 'zonebox')
    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
    .forEach((s) => {
      if (!inView(s)) return;
      ctx.save();
      if (s.rotation) {
        const cx = s.x + s.w / 2;
        const cy = s.y + s.h / 2;
        ctx.translate(cx, cy);
        ctx.rotate((s.rotation * Math.PI) / 180);
        ctx.translate(-cx, -cy);
      }
      drawFn(ctx, s);
      ctx.restore();
      // Labels are drawn OUTSIDE the rotation transform: rotating a symbol must
      // not print its name upside-down or sideways.
      drawLabel(ctx, s);
      // Hover ring.
      if (S.hoveredId === s.id && !selSet.has(s.id)) {
        ctx.save();
        ctx.strokeStyle = 'rgba(0,120,212,0.4)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([3, 3]);
        ctx.strokeRect(s.x - 3, s.y - 3, s.w + 6, s.h + 6);
        ctx.setLineDash([]);
        ctx.restore();
      }
      // Lock indicator.
      if (s.locked) {
        ctx.save();
        ctx.fillStyle = 'rgba(255,180,0,0.85)';
        ctx.fillRect(s.x + s.w - 12, s.y - 2, 10, 10);
        ctx.fillStyle = '#000';
        ctx.font = 'bold 8px Segoe UI';
        ctx.textAlign = 'center';
        ctx.fillText('🔒', s.x + s.w - 7, s.y + 5);
        ctx.restore();
      }
      // Selection + resize handles.
      if (selSet.has(s.id)) {
        ctx.save();
        ctx.strokeStyle = '#0078d4';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 3]);
        ctx.strokeRect(s.x - 4, s.y - 4, s.w + 8, s.h + 8);
        ctx.setLineDash([]);
        if (!s.locked) {
          getHandles(s).forEach((h) => {
            ctx.fillStyle = '#0078d4';
            ctx.fillRect(h.x - 4, h.y - 4, 8, 8);
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 1;
            ctx.strokeRect(h.x - 4, h.y - 4, 8, 8);
          });
        }
        ctx.restore();
      }
    });
  // Smart guides.
  if (S.smartGuides.length) {
    ctx.save();
    ctx.strokeStyle = '#ff00aa';
    ctx.lineWidth = 1 / zoom;
    ctx.setLineDash([4 / zoom, 3 / zoom]);
    S.smartGuides.forEach((g) => {
      ctx.beginPath();
      if (g.type === 'v') {
        ctx.moveTo(g.x!, g.y1!);
        ctx.lineTo(g.x!, g.y2!);
      } else {
        ctx.moveTo(g.x1!, g.y!);
        ctx.lineTo(g.x2!, g.y!);
      }
      ctx.stroke();
    });
    ctx.setLineDash([]);
    ctx.restore();
  }
  // Connect preview.
  if (S.isConnecting && S.connectFrom && S.connectPreview) {
    const fp = getPorts(S.connectFrom.shape)[S.connectFrom.port || 'E'];
    ctx.strokeStyle = 'rgba(0,120,212,0.7)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 3]);
    ctx.beginPath();
    ctx.moveTo(fp.x, fp.y);
    ctx.lineTo(S.connectPreview.x, S.connectPreview.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  // Port circles (connect tool).
  if (S.tool === 'connect') {
    S.shapes.forEach((s) => {
      // A shape may alias several cardinals onto one real face (an elbow has two
      // faces, not four) — draw each distinct face once.
      const seen: { x: number; y: number }[] = [];
      Object.values(getPorts(s)).forEach((p) => {
        if (seen.some((q) => Math.hypot(q.x - p.x, q.y - p.y) < 0.5)) return;
        seen.push(p);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,170,0,0.25)';
        ctx.fill();
        ctx.strokeStyle = '#00aa00';
        ctx.lineWidth = 1.2;
        ctx.stroke();
      });
    });
  }
  // Lasso rectangle.
  if (S.lassoRect) {
    ctx.strokeStyle = 'rgba(0,120,212,0.8)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 2]);
    ctx.strokeRect(S.lassoRect.x, S.lassoRect.y, S.lassoRect.w, S.lassoRect.h);
    ctx.fillStyle = 'rgba(0,120,212,0.06)';
    ctx.fillRect(S.lassoRect.x, S.lassoRect.y, S.lassoRect.w, S.lassoRect.h);
    ctx.setLineDash([]);
  }
  ctx.restore();

  // Drag ghost (library item being placed).
  if (S.dragLib) {
    const w = toW(S.dragLib.sx, S.dragLib.sy);
    const def = registry.getDef(S.dragLib.type) || { w: 60, h: 40, label: '', g: '' };
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.setTransform(dpr * zoom, 0, 0, dpr * zoom, dpr * panX, dpr * panY);
    const gs = {
      id: '__ghost__',
      type: S.dragLib.type,
      x: w.x - def.w / 2,
      y: w.y - def.h / 2,
      w: def.w,
      h: def.h,
      label: '',
      fillColor: '#ffffff',
      strokeColor: '#1a1a1a',
      strokeWidth: 1.5,
      fontSize: 10,
      labelPos: 'bottom' as const,
      tag: '',
      notes: '',
      zIndex: 0,
    };
    try {
      drawFn(ctx, gs);
    } catch {
      /* ghost draw failure is non-fatal */
    }
    ctx.restore();
  }

  // Bookkeeping. NOTE: we deliberately do NOT emit('status') here — drawAll
  // runs every dirty frame and that would re-render the status bar at 60fps.
  // Status (coords/counts) is emitted from pointer moves and doc changes instead.
  updateScrollbars();
  drawMinimap();
}
